import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorEmptyState } from '@/components/ui/empty-state';
import { SkeletonForm } from '@/components/ui/skeleton';
import { useDeliveryConfigQuery } from '@/components/settings/delivery-config-query';
import { useCrmUpdatesStep } from '@/features/analysis-setup/hooks/useCrmUpdatesStep';
import { CrmUpdatesStep } from '@/features/analysis-setup/crm-updates/CrmUpdatesStep';

export const Route = createFileRoute('/analysis-setup/crm-updates')({
  component: AnalysisSetupCrmUpdatesStep,
});

// Whether the step exists is `crmUpdatesStepApplies`, read through
// `useCrmUpdatesStep` — the same reader the hub's strip and chip use; other
// vendors have nothing to configure here and bounce back to the hub overview.
function AnalysisSetupCrmUpdatesStep() {
  const { t } = useTranslation(['analysisSetup']);
  const step = useCrmUpdatesStep();

  // The whole step reads one delivery config (`['settings','delivery-config',
  // org]`): the destination cards, the field table, the actions and the save
  // panel. The step owes the reader its loading and error states.
  const configQuery = useDeliveryConfigQuery(step.known && step.applies);

  // The hub keeps this step VISIBLE while the source is unknown, on purpose:
  // hiding it on a failed lookup removed the only door to it for the rest of
  // the session (`useVisibleSteps`). That decision makes this route the one
  // that owes the reader an answer — and returning `null` here is exactly what
  // a door leading nowhere looks like. There are no retries on this query, so
  // the blank panel was permanent, not transient. Only a failure with nothing
  // to show counts: a failed background refetch keeps the answer it had.
  if (step.error === 'source') {
    return (
      <div data-testid="crm-updates-source-error">
        <ErrorEmptyState
          title={t('analysisSetup:crmUpdates.sourceErrorTitle')}
          description={t('analysisSetup:crmUpdates.sourceErrorDescription')}
          onRetry={step.retry}
        />
      </div>
    );
  }
  // Undetermined yet (the source query hasn't settled) — don't redirect on a guess.
  if (step.loading) return <StepLoading />;

  // Redirect when the step does not apply: the source's CRM takes neither
  // field writes nor a timeline report.
  if (!step.applies) return <Navigate to="/analysis-setup" />;

  // An error with the config in hand is a failed refetch, not an empty step.
  if (configQuery.isError && !configQuery.data)
    return <StepError onRetry={() => void configQuery.refetch()} />;
  if (configQuery.data === undefined) return <StepLoading />;
  // `null` — the organization has no delivery settings yet (`configured:
  // false`): no CRM to write to, which is an answer, not a loading state.
  if (configQuery.data === null) return <StepNotConnected />;

  return <CrmUpdatesStep vendor={step.vendor ?? ''} config={configQuery.data} />;
}

function StepLoading() {
  return (
    <div data-testid="crm-updates-loading">
      <SkeletonForm fields={2} />
    </div>
  );
}

function StepNotConnected() {
  const { t } = useTranslation(['settings']);
  return (
    <div data-testid="crm-updates-not-connected">
      <EmptyState
        title={t('settings:crmIntegrationSection.notConnected')}
        description={t('settings:crmIntegrationSection.connectViaDataSource')}
      />
    </div>
  );
}

function StepError({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation(['analysisSetup']);
  return (
    <div data-testid="crm-updates-settings-error">
      <ErrorEmptyState
        title={t('analysisSetup:crmUpdates.settingsErrorTitle')}
        description={t('analysisSetup:crmUpdates.settingsErrorDescription')}
        onRetry={onRetry}
      />
    </div>
  );
}
